import { Link } from "react-router-dom";
import { PageHero, SectionHeader } from "@/components/PageChrome";
import CTABanner from "@/components/CTABanner";
import { useSEO } from "@/lib/seo";
import { Mail } from "lucide-react";

const ATTORNEYS = [
    {
        n: "01",
        name: "Jack Shelton",
        role: "Founder · Estate, Real Estate & Business Law",
        photo: "/images/jack-shelton.jpeg",
        bio: [
            "Jack Shelton represents individuals, families, and closely-held companies on the estate, real property, and business matters that shape long-term outcomes, from a single negotiated agreement to ongoing representation as outside general counsel.",
            "Earlier in his career, he advised domestic and international clients on complex cross-border transactions and federal regulatory matters in highly regulated industries. He brings that same attention to documentation and structure to Chesterton’s estate, real estate, and closely-held business practice.",
        ],
        education: [
            "LL.M., Admiralty and Maritime Law, Tulane University Law School, 2011 (cum laude)",
            "J.D., University of Tennessee College of Law, 2010 (cum laude)",
            "B.A., Colorado College, 2004",
        ],
        admissions: ["Tennessee"],
        affiliationsCurrent: [
            "Tennessee Bar Association (TBA)",
            "Knights of Columbus",
            "Society for International Affairs (SIA)",
            "Air & Space Forces Association (AFA)",
            "Space Force Association (SFA)",
            "National Defense Industrial Association (NDIA)",
        ],
        affiliationsPrior: [
            "Co-founder, Aegis Space Law",
            "Co-founder, Association of Commercial Space Professionals",
            "Lead Instructor, Export Compliance Training Institute",
            "Adjunct Professor, University of Mississippi School of Law (U.S. Export Controls, Fall 2025)",
        ],
        email: "jack@chesterton.law",
        testId: "attorney-jack-shelton",
    },
];

function LabeledList({ label, items }) {
    if (!items || !items.length) return null;
    return (
        <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-acre-secondary mb-3">{label}</div>
            <ul className="space-y-1.5 text-sm text-acre-ink">
                {items.map((e, i) => (<li key={i}>{e}</li>))}
            </ul>
        </div>
    );
}

function AttorneyBlock({ a }) {
    return (
        <section
            data-testid={a.testId}
            className="py-16 lg:py-20 border-b border-acre-line last:border-b-0"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                <div className="lg:col-span-4">
                    <div className="text-[11px] uppercase tracking-[0.28em] text-acre-muted mb-6">
                        {a.n}
                    </div>
                    <div className="relative aspect-[4/5] bg-[#FBFAF8] border border-acre-line flex items-center justify-center overflow-hidden">
                        {a.photo ? (
                            <img src={a.photo} alt={a.name} className="w-full h-full object-cover" />
                        ) : (
                            <div className="text-center px-6">
                                <div className="font-serif-display text-6xl">C</div>
                                <div className="mt-3 text-[10px] uppercase tracking-[0.25em] text-acre-muted">
                                    Attorney photo<br />coming soon
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mt-8 flex flex-col gap-3">
                        <a
                            href={`mailto:${a.email}`}
                            data-testid={`${a.testId}-email`}
                            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-acre-primary border-b border-acre-secondary pb-1 hover:text-acre-secondary transition-colors w-fit"
                        >
                            <Mail size={13} /> {a.email}
                        </a>
                        <Link
                            to="/contact"
                            data-testid={`${a.testId}-contact`}
                            className="inline-flex items-center justify-center gap-2 bg-acre-primary text-white px-6 py-3 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-acre-primaryHover transition-colors w-fit"
                        >
                            Book a Consultation
                        </Link>
                    </div>
                </div>

                <div className="lg:col-span-8">
                    <h2 className="font-serif-display text-4xl lg:text-6xl text-acre-secondary leading-[1.02]">
                        {a.name}
                    </h2>
                    <div className="mt-4 text-sm uppercase tracking-[0.2em] text-acre-muted">{a.role}</div>

                    <div className="mt-8 space-y-5 text-base lg:text-lg leading-relaxed text-acre-muted max-w-2xl">
                        {a.bio.map((p, i) => (<p key={i}>{p}</p>))}
                    </div>

                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
                        <LabeledList label="Education" items={a.education} />
                        <LabeledList label="Bar Admissions" items={a.admissions} />
                        <LabeledList label="Affiliations" items={a.affiliationsCurrent} />
                        <LabeledList label="Prior Positions" items={a.affiliationsPrior} />
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function People() {
    useSEO({
        title: "People",
        description:
            "The attorneys of Chesterton. Biographies, credentials, and contact information.",
        path: "/people",
    });

    return (
        <>
            <PageHero
                testId="people-hero"
                kicker="People"
                title={<>Our attorneys.</>}
                intro="Every engagement is handled by the attorney you hire, from first conversation through final signature."
            />

            <div className="bg-white border-t border-acre-line">
                {ATTORNEYS.map((a) => (<AttorneyBlock key={a.n} a={a} />))}
            </div>

            {ATTORNEYS.length === 1 && (
                <section className="bg-acre-bg py-16 lg:py-20 border-t border-acre-line">
                    <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
                        <SectionHeader
                            kicker="Joining the Firm"
                            title={<>Room for more.</>}
                            description="Chesterton is selectively adding attorneys with practice depth in estate, real property, and closely-held business matters. Inquiries are welcome by email at info@chesterton.law."
                        />
                    </div>
                </section>
            )}

            <CTABanner />
        </>
    );
}
